import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Camera, Trash2 } from "lucide-react";
import { ProvinciaSelect } from "@/components/ProvinciaSelect";
import { toast } from "sonner";

const GRADI = [
  "Dirigente Superiore", "Dirigente", "Com. Territoriale", "Com. Distaccamento",
  "Sottufficiale", "Agente", "Agente scelto", "Operatore",
];

const EMPTY = {
  nome: "", cognome: "", codice_fiscale: "", matricola: "", distaccamento: "", provincia: "",
  numero_tessera: "", telefono: "", email: "", ruolo_grado: "", stato: "attiva",
  scadenza_nomina: "", scadenza_tesserino: "", numero_decreto: "", data_rilascio_decreto: "",
  quota_pagata_anno: null,
};

export function GuardiaFormDialog({ initial = null, trigger }) {
  const isEdit = Boolean(initial?.id);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [foto, setFoto] = useState(null);
  const fotoRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, initial[k] ?? EMPTY[k]])) } : EMPTY);
      setFoto(initial?.foto ?? null);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const currentYear = new Date().getFullYear();

  const onFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setFoto(await resizeImage(file, 400, 520, 0.85));
    } catch {
      toast.error("Errore nel caricamento della foto");
    }
  };

  const submit = async () => {
    if (!form.nome.trim() || !form.cognome.trim() || !form.codice_fiscale.trim()) {
      toast.error("Nome, cognome e Codice Fiscale sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        codice_fiscale: form.codice_fiscale.trim().toUpperCase(),
        scadenza_nomina: form.scadenza_nomina || null,
        scadenza_tesserino: form.scadenza_tesserino || null,
        numero_decreto: form.numero_decreto || null,
        data_rilascio_decreto: form.data_rilascio_decreto || null,
        foto: foto,
      };
      if (isEdit) {
        await api.patch(`/guardie/${initial.id}`, payload);
        toast.success("Anagrafica aggiornata");
        qc.invalidateQueries({ queryKey: ["guardia", initial.id] });
      } else {
        await api.post("/guardie", payload);
        toast.success("Guardia creata");
      }
      qc.invalidateQueries({ queryKey: ["guardie"] });
      qc.invalidateQueries({ queryKey: ["distaccamenti"] });
      qc.invalidateQueries({ queryKey: ["scadenze"] });
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto gz-scroll">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica anagrafica guardia" : "Nuova guardia"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4 rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="flex h-24 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-white">
              {foto ? <img src={foto} alt="Foto guardia" className="h-full w-full object-cover" /> : <Camera className="h-6 w-6 text-slate-300" />}
            </div>
            <div className="space-y-2">
              <Label>Foto della guardia (per il tesserino)</Label>
              <div className="flex items-center gap-2">
                <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={onFoto} data-testid="guardia-foto-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fotoRef.current?.click()} data-testid="guardia-foto-upload">
                  <Camera className="mr-2 h-4 w-4" /> {foto ? "Cambia foto" : "Carica foto"}
                </Button>
                {foto && (
                  <Button type="button" size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setFoto(null)} data-testid="guardia-foto-remove">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-400">JPG/PNG · verrà ridimensionata automaticamente.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Nome *</Label><Input data-testid="guardia-nome" value={form.nome} onChange={set("nome")} /></div>
            <div className="space-y-2"><Label>Cognome *</Label><Input data-testid="guardia-cognome" value={form.cognome} onChange={set("cognome")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Codice Fiscale *</Label><Input data-testid="guardia-cf" value={form.codice_fiscale} onChange={set("codice_fiscale")} className="font-mono-code uppercase" /></div>
            <div className="space-y-2"><Label>Matricola</Label><Input data-testid="guardia-matricola" value={form.matricola} onChange={set("matricola")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Distaccamento operativo</Label><Input data-testid="guardia-distaccamento" value={form.distaccamento} onChange={set("distaccamento")} /></div>
            <div className="space-y-2">
              <Label>Provincia di appartenenza</Label>
              <ProvinciaSelect value={form.provincia} onChange={(v) => setForm((f) => ({ ...f, provincia: v }))} testid="guardia-provincia" emptyLabel="Proponi dal distaccamento" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Numero tessera</Label><Input data-testid="guardia-tessera" value={form.numero_tessera} onChange={set("numero_tessera")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Ruolo / Grado</Label>
              <Select value={form.ruolo_grado || undefined} onValueChange={(v) => setForm((f) => ({ ...f, ruolo_grado: v }))}>
                <SelectTrigger data-testid="guardia-ruolo-grado"><SelectValue placeholder="Seleziona grado" /></SelectTrigger>
                <SelectContent>
                  {GRADI.map((gr) => <SelectItem key={gr} value={gr}>{gr}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Telefono</Label><Input data-testid="guardia-telefono" value={form.telefono} onChange={set("telefono")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Email</Label><Input data-testid="guardia-email" type="email" value={form.email} onChange={set("email")} /></div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={form.stato || "attiva"} onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}>
                <SelectTrigger data-testid="guardia-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attiva">Attiva</SelectItem>
                  <SelectItem value="disattivata">Non attiva</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#14382C]"
              data-testid="guardia-quota-pagata"
              checked={form.quota_pagata_anno === currentYear}
              onChange={(e) => setForm((f) => ({ ...f, quota_pagata_anno: e.target.checked ? currentYear : null }))}
            />
            <Label className="cursor-pointer">Quota associativa {currentYear} pagata <span className="font-normal text-slate-400">(registra il pagamento · si azzera il 31 dicembre)</span></Label>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Scadenze</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Scadenza nomina</Label><Input type="date" data-testid="guardia-scadenza-nomina" value={form.scadenza_nomina || ""} onChange={set("scadenza_nomina")} /></div>
              <div className="space-y-2"><Label>Scadenza tesserino</Label><Input type="date" data-testid="guardia-scadenza-tesserino" value={form.scadenza_tesserino || ""} onChange={set("scadenza_tesserino")} /></div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Decreto (validità 2 anni · promemoria 60 gg prima)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Numero decreto</Label><Input data-testid="guardia-numero-decreto" placeholder="es. DEC-2024-0012" value={form.numero_decreto || ""} onChange={set("numero_decreto")} /></div>
              <div className="space-y-2">
                <Label>Data di rilascio decreto</Label>
                <Input type="date" data-testid="guardia-rilascio-decreto" value={form.data_rilascio_decreto || ""} onChange={set("data_rilascio_decreto")} />
                {form.data_rilascio_decreto && (
                  <p className="text-xs text-slate-500">
                    Scadenza calcolata: {(() => { const d = new Date(form.data_rilascio_decreto); d.setFullYear(d.getFullYear() + 2); return d.toLocaleDateString("it-IT"); })()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-guardia-button" className="bg-[#14382C] hover:bg-[#0E281F]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {isEdit ? "Salva modifiche" : "Crea guardia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
